import { useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router';
import { useValidateResetToken, useResetPassword } from '../hooks/usePassword';
import { Button } from '@/components/ui/button';
import { PasswordInput } from '@/shared/components/PasswordInput';
import { Field, FieldLabel } from '@/components/ui/field';
import { Card, CardContent } from '@/components/ui/card';
import { LoadingAnimation } from '@/shared/components/LoadingAnimation';

export const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const navigate = useNavigate();

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const validateMutation = useValidateResetToken(token || '');
  const resetMutation = useResetPassword();

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);

    if (!token) {
      setFormError('Invalid reset link');
      return;
    }

    if (password !== confirmPassword) {
      setFormError('Passwords do not match');
      return;
    }

    resetMutation.mutate(
      { token, newPassword: password },
      {
        onSuccess: () => {
          setSubmitted(true);
        },
      }
    );
  };

  if (!token) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-bold">Invalid Link</h2>
                <p className="text-gray-600">
                  This password reset link is invalid or expired.
                </p>
                <Link to="/forgot-password" className="text-blue-600 hover:underline">
                  Request a new link
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (validateMutation.isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div>Validating reset link...</div>
      </div>
    );
  }

  if (validateMutation.error || !validateMutation.data) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-bold">Link Expired</h2>
                <p className="text-gray-600">
                  This password reset link has expired.
                </p>
                <Link to="/forgot-password" className="text-blue-600 hover:underline">
                  Request a new link
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="w-full max-w-md">
          <Card>
            <CardContent className="pt-6">
              <div className="space-y-4 text-center">
                <h2 className="text-2xl font-bold">Password Reset</h2>
                <p className="text-gray-600">
                  Your password has been reset successfully.
                </p>
                <Link to="/login" className="text-blue-600 hover:underline">
                  Login with your new password
                </Link>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center justify-center min-h-screen">
      {resetMutation.isPending && (
        <LoadingAnimation fullScreen className="z-[100]" text="Resetting password and sending confirmation…" />
      )}
      <div className="w-full max-w-md">
        <Card>
          <CardContent className="pt-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="text-center mb-6">
                <h1 className="text-2xl font-bold">Reset Password</h1>
                <p className="text-gray-600 text-sm mt-2">
                  Enter your new password
                </p>
              </div>

              {resetMutation.error && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                  {resetMutation.error.message}
                </div>
              )}

              {formError && (
                <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
                  {formError}
                </div>
              )}

              <Field>
                <FieldLabel htmlFor="password">New Password</FieldLabel>
                <PasswordInput
                  id="password"
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  required
                  disabled={resetMutation.isPending}
                />
              </Field>

              <Field>
                <FieldLabel htmlFor="confirmPassword">Confirm Password</FieldLabel>
                <PasswordInput
                  id="confirmPassword"
                  value={confirmPassword}
                  onChange={(e) => {
                    setConfirmPassword(e.target.value);
                    if (formError) setFormError(null);
                  }}
                  required
                  disabled={resetMutation.isPending}
                />
              </Field>

              <Button
                type="submit"
                className="w-full"
                disabled={resetMutation.isPending}
              >
                {resetMutation.isPending ? 'Resetting...' : 'Reset Password'}
              </Button>

              <div className="text-center text-sm">
                <Link to="/login" className="text-blue-600 hover:underline">
                  Back to login
                </Link>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
